/*
do not need review have not yet been integrated
*/
import {
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Download,
  Eye,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import type { ArtifactGeneration } from '@/hooks/useArtifactGeneration'
import type { ArtifactType } from '@/types/api'

// ---------- Label helpers ----------

const TYPE_LABELS: Record<ArtifactType, string> = {
  business_model_canvas: 'Business Model Canvas',
  lean_canvas: 'Lean Canvas',
  user_persona: 'User Persona',
  pitch_deck: 'Pitch Deck',
  one_pager: 'One-Pager',
  competitor_analysis: 'Competitor Analysis',
  go_to_market_strategy: 'Go-to-Market Strategy',
  product_roadmap: 'Product Roadmap',
  financial_model: 'Financial Model',
}

function typeLabel(type: ArtifactType): string {
  return TYPE_LABELS[type] ?? type.replace(/_/g, ' ')
}

// ---------- Status badge ----------

function StatusBadge({ status }: { status: ArtifactGeneration['status'] }) {
  switch (status) {
    case 'requesting':
    case 'generating':
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full">
          <Loader2 className="h-3 w-3 animate-spin" />
          {status === 'requesting' ? 'Starting...' : 'Generating...'}
        </span>
      )
    case 'completed':
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full">
          <CheckCircle2 className="h-3 w-3" />
          Completed
        </span>
      )
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-400 bg-red-400/10 border border-red-400/20 px-2 py-0.5 rounded-full">
          <AlertCircle className="h-3 w-3" />
          Failed
        </span>
      )
    default:
      return null
  }
}

// ---------- Component ----------

interface ArtifactCardProps {
  generation: ArtifactGeneration
  onView?: (artifactId: string) => void
  onDismiss?: (artifactId: string) => void
}

/**
 * Inline card for artifact generation: shows status, preview snippet, and actions.
 * Renders within the chat message stream.
 */
export function ArtifactCard({ generation, onView, onDismiss }: ArtifactCardProps) {
  const { artifactId, type, status, artifact, error } = generation
  const isActive = status === 'requesting' || status === 'generating'

  /** Truncated content preview (first ~300 chars). */
  const preview = artifact?.content
    ? artifact.content.length > 300
      ? artifact.content.slice(0, 300) + '...'
      : artifact.content
    : null

  return (
    <Card
      className={cn(
        'w-full max-w-lg border bg-card/60 backdrop-blur-sm shadow-lg overflow-hidden transition-all duration-300',
        isActive && 'border-amber-400/30 animate-pulse',
        status === 'completed' && 'border-emerald-400/20',
        status === 'failed' && 'border-red-400/20'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'h-9 w-9 rounded-lg flex items-center justify-center',
              isActive ? 'bg-amber-400/10 text-amber-400' : 'bg-primary/10 text-primary'
            )}
          >
            <FileText className="h-4 w-4" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-foreground leading-tight">
              {artifact?.title || typeLabel(type)}
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              {artifact?.description || `Generating ${typeLabel(type).toLowerCase()}...`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={status} />
          {onDismiss && status !== 'generating' && status !== 'requesting' && (
            <button
              onClick={() => onDismiss(artifactId)}
              className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Content preview */}
      {preview && (
        <div className="px-4 py-3 bg-black/10">
          <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono leading-relaxed max-h-40 overflow-y-auto">
            {preview}
          </pre>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="px-4 py-3 bg-red-500/5">
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Generating skeleton */}
      {isActive && !preview && (
        <div className="px-4 py-3 space-y-2">
          <div className="h-3 w-3/4 bg-secondary/60 rounded animate-pulse" />
          <div className="h-3 w-1/2 bg-secondary/60 rounded animate-pulse" />
          <div className="h-3 w-5/6 bg-secondary/60 rounded animate-pulse" />
        </div>
      )}

      {/* Actions */}
      {status === 'completed' && (
        <div className="flex items-center gap-2 px-4 py-3 border-t border-white/5">
          {onView && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => onView(artifactId)}
            >
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              View
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => {
              if (!artifact?.content) return
              const blob = new Blob([artifact.content], { type: 'text/markdown' })
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `${typeLabel(type).replace(/\s+/g, '_').toLowerCase()}.md`
              a.click()
              URL.revokeObjectURL(url)
            }}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Download
          </Button>
        </div>
      )}
    </Card>
  )
}
